import Popup from "../Elements/Popup";
import Card from "./Card";
import { useContext, useState, useEffect } from "react";
import { YearContext, AuthContext } from "../../context/context";
import { TbPaperclip, TbEdit, TbTrash, TbCirclePlus, TbPlus } from "react-icons/tb";
import { submitTheme, deleteTheme, updateTheme } from "../../api";
import animation from "../../Animations/theme-animation.json";
import InputTextarea from "../Inputs/InputTextarea";
import { useLanguage } from "../../context/LanguageContext";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Toast from "../Elements/Toast";
import CardMobile from "./CardMobile";

const themeSchema = z.object({
  theme: z
    .string()
    .min(1, "Theme cannot be empty.")
    .max(200, "Theme must be 200 characters or less"),
});

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

const CardTheme = () => {
  const { selectedYear } = useContext(YearContext);
  const { userData, setUserData } = useContext(AuthContext);
  const { copy } = useLanguage();
  const isMobile = useIsMobile();

  const [popupType, setPopupType] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const openPopup = (type) => setPopupType(type);
  const closePopup = () => {
    reset();
    setPopupType(null);
    setThemeEditError("");
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(themeSchema),
    defaultValues: {
      theme: "",
    },
  });

  const [themeToEdit, setThemeToEdit] = useState(null);
  const [editedThemeText, setEditedThemeText] = useState("");
  const [themeEditError, setThemeEditError] = useState("");

  const handleSubmitTheme = async (data) => {
    try {
      const newTheme = await submitTheme(data.theme, userData.id, selectedYear);

      if (!newTheme) {
        setToast({
          message: copy.card_theme.errors.submit_error,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.themes.push(newTheme);
      setUserData(newUserData);

      reset();
      setToast({
        message: copy.card_theme.errors.update_success,
        type: "success",
      });
      closePopup();
    } catch (error) {
      console.error("Error submitting theme:", error.message);
      setToast({ message: copy.card_theme.errors.submit_error, type: "error" });
    }
  };

  const themeData = selectedYear
    ? userData?.themes?.filter((item) => item.year === selectedYear)
    : userData?.themes;

  const [themeIdToDelete, setThemeIdToDelete] = useState(null);

  const handleDeleteClickTheme = (themeId) => {
    setThemeIdToDelete(themeId);
    openPopup("theme-delete");
  };

  const handleDeleteTheme = async () => {
    if (!themeIdToDelete) {
      setToast({ message: copy.card_theme.errors.not_found, type: "error" });
      return;
    }

    try {
      const result = await deleteTheme(themeIdToDelete);

      if (!result) {
        setToast({
          message: copy.card_theme.errors.delete_failed,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.themes = newUserData.themes.filter(
        (t) => t.id !== themeIdToDelete
      );
      setUserData(newUserData);

      setToast({
        message: copy.card_theme.errors.delete_success,
        type: "success",
      });
      setThemeIdToDelete(null);
      closePopup();
    } catch (err) {
      console.error("Error deleting theme:", err.message);
      setToast({
        message: copy.card_theme.errors.delete_failed,
        type: "error",
      });
    }
  };

  const handleEditClickTheme = (themeId, themeText) => {
    setThemeToEdit(themeId);
    setEditedThemeText(themeText);
    openPopup("theme-edit");
  };

  const handleEditTheme = async () => {
    const result = themeSchema.safeParse({ theme: editedThemeText });

    if (!result.success) {
      const message =
        result.error.format().theme?._errors?.[0] ||
        copy.card_theme.errors.empty;
      setThemeEditError(message);
      return;
    }

    try {
      const updatedTheme = await updateTheme(themeToEdit, {
        theme: editedThemeText,
      });

      if (!updatedTheme) {
        setToast({
          message: copy.card_theme.errors.update_failed,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.themes = newUserData.themes.map((t) =>
        t.id === themeToEdit ? { ...t, theme: updatedTheme.theme } : t
      );

      setUserData(newUserData);
      setToast({
        message: copy.card_theme.errors.update_success,
        type: "success",
      });
      setEditedThemeText("");
      setThemeToEdit(null);
      closePopup();
    } catch (err) {
      console.error("Error updating theme:", err.message);
      setToast({
        message: copy.card_theme.errors.update_failed,
        type: "error",
      });
    }
  };

  const addThemeDropdown = [
    {
      text: copy.dropdown.add,
      onClick: () => openPopup("theme"),
      icon: <TbCirclePlus />,
    },
  ];

  const themeDropdown = (themeId, themeText) => [
    {
      text: copy.dropdown.edit,
      onClick: () => handleEditClickTheme(themeId, themeText),
      icon: <TbEdit />,
    },
    {
      text: copy.dropdown.delete,
      onClick: () => handleDeleteClickTheme(themeId),
      icon: <TbTrash />,
    },
  ];

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}

      {/* Mobile Card */}
      {isMobile &&
        (themeData && themeData.length > 0 ? (
          <CardMobile
            title={copy.card_theme.headline}
            dropdownOptions={themeDropdown(themeData[0].id, themeData[0].theme)}
          >
            <p className="w-full text-left text-base mt-2 mb-1">
              {themeData[0].theme}
            </p>
          </CardMobile>
        ) : (
          <CardMobile
            title={copy.card_theme.headline}
            dropdownOptions={addThemeDropdown}
            contentBgColor="bg-blue-100"
          >
            <div className="py-4">
              <button
                className="w-10 h-10 rounded-md flex items-center justify-center shadow-sm bg-white card-base-color transition-colors duration-200 mt-2 mx-auto"
                onClick={() => openPopup("theme")}
                aria-label={copy.card_theme.add_button}
              >
                <TbPlus className="text-2xl" />
              </button>
            </div>
          </CardMobile>
        ))}

      {/* Desktop Card(s) */}
      {!isMobile &&
        (themeData && themeData.length > 0 ? (
          themeData.map((themeItem) => (
            <Card
              key={themeItem.id}
              iconTop={TbPaperclip}
              title={copy.card_theme.headline}
              description={copy.card_theme.description}
              options={themeDropdown(themeItem.id, themeItem.theme)}
              onClick={() => openPopup("theme")}
              cardClassName="h-full"
            >
              <div className="flex flex-col w-full h-full justify-center gap-1 px-4">
                <p className="text-center">{themeItem.theme}</p>
              </div>
            </Card>
          ))
        ) : (
          <Card
            iconTop={TbPaperclip}
            title={copy.card_theme.headline}
            description={copy.card_theme.description}
            lottieAnimation={animation}
            startButton={`${copy.card_theme.add_button} ${selectedYear}`}
            onClick={() => openPopup("theme")}
            options={addThemeDropdown}
            className="justify-center"
            cardClassName="h-full"
          />
        ))}

      {/* Add Theme */}
      <Popup
        isOpen={popupType === "theme"}
        onClose={closePopup}
        title={`${copy.card_theme.popup.add_title} ${selectedYear}`}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={() => {
          document
            .getElementById("theme-form")
            ?.dispatchEvent(
              new Event("submit", { bubbles: true, cancelable: true })
            );
        }}
      >
        <form id="theme-form" onSubmit={handleSubmit(handleSubmitTheme)}>
          <div className="flex flex-col gap-1">
            <InputTextarea
              label={copy.card_theme.form.label}
              placeholder={copy.card_theme.form.placeholder}
              {...register("theme")}
              error={errors.theme?.message}
              details={`${watch("theme")?.length || 0}/200 characters`}
            />
          </div>
        </form>
      </Popup>

      {/* Delete Theme */}
      <Popup
        isOpen={popupType === "theme-delete"}
        onClose={closePopup}
        title={copy.card_theme.popup.delete_title}
        description={copy.card_theme.popup.delete_description}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.delete}
        onClickPrimaryLink={handleDeleteTheme}
      />

      {/* Edit Theme */}
      <Popup
        isOpen={popupType === "theme-edit"}
        onClose={closePopup}
        title={copy.card_theme.popup.edit_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={handleEditTheme}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <InputTextarea
              label={copy.card_theme.form.label}
              placeholder={copy.card_theme.form.placeholder}
              value={editedThemeText}
              onChange={(e) => setEditedThemeText(e.target.value)}
              error={themeEditError}
              details={`${editedThemeText.length}/200 characters`}
            />
          </div>
        </div>
      </Popup>
    </>
  );
};

export default CardTheme;
